import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js'

import Sizes from './Utils/Sizes.js'
import Time from './Utils/Time.js'
import Resources from './Resources.js'
import Camera from './Camera.js'
import World from './World/index.js'
import BlurPass from './Passes/Blur.js'
import GlowsPass from './Passes/Glows.js'
import Music from './Music.js'
import Menu from './Menu.js'
import SaveSystem from './Game/SaveSystem.js'

export default class Application
{
    constructor(_options)
    {
        this.$canvas = _options.$canvas
        this.time = new Time()
        this.sizes = new Sizes()
        this.resources = new Resources()
        this.music = new Music()
        this.menu = new Menu({
            music: this.music,
            onAdvanceMilestone: () =>
            {
                const result = this.world?.state.advanceMilestone() ?? 'Game is still loading. Try again shortly.'
                this.world?.autosave.save()
                return result
            },
            onRestart: () =>
            {
                if(this.world)
                    this.world.autosave.clear()
                else
                    SaveSystem.clear()
                window.location.reload()
            }
        })
        this.scene = new THREE.Scene()
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.$canvas,
            antialias: true,
            alpha: false,
            powerPreference: 'high-performance'
        })
        this.renderer.setClearColor(0xf5ad72, 1)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        this.renderer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
        this.renderer.outputColorSpace = THREE.SRGBColorSpace
        this.renderer.autoClear = false

        this.camera = new Camera({ time: this.time, sizes: this.sizes, renderer: this.renderer })
        this.scene.add(this.camera.container)
        this.setPasses()

        this.resources.on('ready', () =>
        {
            this.world = new World({
                time: this.time,
                sizes: this.sizes,
                resources: this.resources,
                camera: this.camera,
                renderer: this.renderer
            })
            this.scene.add(this.world.container)
            document.body.classList.add('is-ready')
        })
        this.resources.on('progress', (_progress) =>
        {
            const bar = document.querySelector('.js-loading-bar')
            if(bar)
                bar.style.transform = `scaleX(${_progress})`
        })
        this.sizes.on('resize', () =>
        {
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
            this.renderer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
        })
        this.time.on('tick', () => this.passes.composer.render())
    }

    setPasses()
    {
        const composer = new EffectComposer(this.renderer)
        composer.addPass(new RenderPass(this.scene, this.camera.instance))
        const blurStrength = window.matchMedia('(pointer: coarse)').matches ? 0.65 : 1
        const horizontalBlur = new ShaderPass(BlurPass)
        const verticalBlur = new ShaderPass(BlurPass)
        horizontalBlur.material.uniforms.uResolution.value = new THREE.Vector2(this.sizes.viewport.width, this.sizes.viewport.height)
        verticalBlur.material.uniforms.uResolution.value = new THREE.Vector2(this.sizes.viewport.width, this.sizes.viewport.height)
        horizontalBlur.material.uniforms.uStrength.value = new THREE.Vector2(blurStrength, 0)
        verticalBlur.material.uniforms.uStrength.value = new THREE.Vector2(0, blurStrength)
        composer.addPass(horizontalBlur)
        composer.addPass(verticalBlur)

        const glows = new ShaderPass(GlowsPass)
        glows.material.uniforms.uPosition.value = new THREE.Vector2(0, 0.25)
        glows.material.uniforms.uRadius.value = 0.7
        glows.material.uniforms.uColor.value = new THREE.Color('#ffcfe0').convertLinearToSRGB()
        glows.material.uniforms.uAlpha.value = 0.30
        composer.addPass(glows)
        composer.addPass(new OutputPass())
        this.passes = { composer, horizontalBlur, verticalBlur }

        this.sizes.on('resize', () =>
        {
            composer.setSize(this.sizes.viewport.width, this.sizes.viewport.height)
            horizontalBlur.material.uniforms.uResolution.value.set(this.sizes.viewport.width, this.sizes.viewport.height)
            verticalBlur.material.uniforms.uResolution.value.set(this.sizes.viewport.width, this.sizes.viewport.height)
        })
    }
}
